export interface CompressedImage {
  blob: Blob;
  width: number;
  height: number;
  sizeBytes: number;
}

/**
 * Calculate output dimensions that fit within maxDimension on the longest side,
 * maintaining aspect ratio. Never upscales.
 */
export function calculateTargetDimensions(
  sourceWidth: number,
  sourceHeight: number,
  maxDimension: number,
): { width: number; height: number } {
  if (sourceWidth <= maxDimension && sourceHeight <= maxDimension) {
    return { width: sourceWidth, height: sourceHeight };
  }
  const ratio = Math.min(maxDimension / sourceWidth, maxDimension / sourceHeight);
  return {
    width: Math.round(sourceWidth * ratio),
    height: Math.round(sourceHeight * ratio),
  };
}

/**
 * Returns true when OffscreenCanvas is available AND its convertToBlob API is
 * callable. iOS 16.x shipped OffscreenCanvas without convertToBlob, which causes
 * a runtime TypeError and — on some Safari versions — produces a Blob that the
 * structured clone algorithm rejects when writing to IndexedDB. We guard both
 * conditions so that affected environments fall through to the HTMLCanvasElement
 * path, which uses the well-supported canvas.toBlob() API instead.
 */
function offscreenCanvasSupported(): boolean {
  return (
    typeof OffscreenCanvas !== 'undefined' &&
    typeof OffscreenCanvas.prototype.convertToBlob === 'function'
  );
}

/**
 * Compress an image to a target max dimension and JPEG quality using Canvas API.
 * Uses OffscreenCanvas when available and fully supported (web worker compatible),
 * falls back to HTMLCanvasElement (always available in browser main thread).
 */
export async function compressImage(
  file: File | Blob,
  options?: {
    maxDimension?: number;
    quality?: number;
    mimeType?: string;
  },
): Promise<CompressedImage> {
  const maxDimension = options?.maxDimension ?? 800;
  const quality = options?.quality ?? 0.8;
  const mimeType = options?.mimeType ?? 'image/jpeg';

  const bitmap = await createImageBitmap(file);
  const { width, height } = calculateTargetDimensions(bitmap.width, bitmap.height, maxDimension);

  if (offscreenCanvasSupported()) {
    const canvas = new OffscreenCanvas(width, height);
    // CAST: OffscreenCanvas always supports 2d context at valid dimensions
    const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await canvas.convertToBlob({ type: mimeType, quality });
    return { blob, width, height, sizeBytes: blob.size };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  // CAST: HTMLCanvasElement always supports 2d context
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('canvas.toBlob returned null'));
      },
      mimeType,
      quality,
    );
  });

  return { blob, width, height, sizeBytes: blob.size };
}

/**
 * Generate a small thumbnail for timeline/grid display.
 * Defaults to 200px max dimension at 70% quality.
 */
export async function generateThumbnail(
  file: File | Blob,
  options?: {
    maxDimension?: number;
    quality?: number;
  },
): Promise<CompressedImage> {
  return compressImage(file, {
    maxDimension: options?.maxDimension ?? 200,
    quality: options?.quality ?? 0.7,
  });
}
